import { useEffect, useState } from 'react';
import {
  Button, FileInput, Group, NumberInput, Select,
  Stack, Stepper, Text, TextInput, Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconReceipt, IconUpload } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import HttpClient from '../helpers/HttpClient';
import { notify } from '../components/NotificationUtils';
import { Booking, BookingType } from '../types/BookingModel';
import { getAccessToken } from '../helpers/TokenRefresh';
import { config } from '../helpers/Config';

export default function BookingWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [persons, setPersons] = useState<{ value: string; label: string }[]>([]);
  const [bookingTypes, setBookingTypes] = useState<{ value: string; label: string }[]>([]);

  // Step 1 fields
  const [bookingDate, setBookingDate] = useState<Date>(new Date());
  const [personId, setPersonId] = useState<string | null>(null);
  const [bookingTypeId, setBookingTypeId] = useState<string | null>(null);
  const [bookingText, setBookingText] = useState('');
  const [bookingAmount, setBookingAmount] = useState<number | string>(0);

  // Step 2 fields
  const [savedBookingId, setSavedBookingId] = useState<number | null>(null);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [documentDate, setDocumentDate] = useState<Date | null>(null);

  useEffect(() => {
    Promise.all([
      new HttpClient<any>('/person').getAll({ slim: 'true' }),
      new HttpClient<BookingType>('/bookingType').getAll(),
    ]).then(([ps, ts]) => {
      setPersons(ps.map((p: any) => ({ value: p.id.toString(), label: `${p.lastName} ${p.firstName}` })));
      setBookingTypes(ts.map((t: any) => ({ value: t.id.toString(), label: t.title })));
    }).finally(() => setLoading(false));
  }, []);

  const handleSaveAndNext = async () => {
    if (!personId || !bookingTypeId || !bookingText.trim()) {
      notify('Fehler', 'Bitte alle Pflichtfelder ausfüllen', 'red');
      return;
    }
    setSaving(true);
    try {
      const res: any = await new HttpClient<Booking>('/booking/batch').post([{
        bookingDate,
        bookingText,
        bookingAmount: Number(bookingAmount),
        person: { id: parseInt(personId) },
        bookingType: { id: bookingTypeId },
      }] as any);
      const id = res?.data?.[0]?.id ?? res?.[0]?.id;
      setSavedBookingId(id ?? null);
      setStep(1);
    } catch {
      notify('Fehler', 'Speichern fehlgeschlagen', 'red');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async () => {
    if (!receiptFile || !savedBookingId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', receiptFile);
      const token = await getAccessToken();
      const params = new URLSearchParams({ bookingId: String(savedBookingId) });
      if (documentDate) params.set('documentDate', documentDate.toISOString());
      await axios.post(`${config.apiUrl}/receipt?${params}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notify('Erfolg', 'Beleg hochgeladen', 'green');
    } catch {
      notify('Fehler', 'Upload fehlgeschlagen', 'red');
    } finally {
      setUploading(false);
    }
    navigate('/booking');
  };

  if (loading) return null;

  return (
    <Stack p="md" maw={560} mx="auto">
      <Title order={2}>Neue Buchung</Title>

      <Stepper active={step} allowNextStepsSelect={false} mt="sm">
        {/* ── Step 1: Booking details ── */}
        <Stepper.Step label="Buchung" description="Details eingeben">
          <Stack mt="md" gap="sm">
            <DatePickerInput
              label="Datum"
              value={bookingDate}
              onChange={(v: any) => setBookingDate(v ? new Date(v) : new Date())}
              valueFormat="DD.MM.YYYY"
              required
            />
            <Select
              label="Person"
              data={persons}
              value={personId}
              onChange={setPersonId}
              searchable
              clearable
              required
            />
            <Select
              label="Buchungsart"
              data={bookingTypes}
              value={bookingTypeId}
              onChange={setBookingTypeId}
              required
            />
            <TextInput
              label="Text"
              value={bookingText}
              onChange={(e) => { const v = e.currentTarget.value; setBookingText(v); }}
              required
            />
            <NumberInput
              label="Betrag (€)"
              value={bookingAmount}
              onChange={setBookingAmount}
              decimalSeparator=","
              prefix="€ "
            />
            <Group mt="xs">
              <Button onClick={handleSaveAndNext} loading={saving}>
                Speichern & Weiter
              </Button>
              <Button variant="subtle" onClick={() => navigate('/booking')}>
                Abbrechen
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>

        {/* ── Step 2: Receipt upload (optional) ── */}
        <Stepper.Step label="Beleg" description="Optional">
          <Stack mt="md" gap="sm">
            <Text c="dimmed" size="sm">
              Buchung gespeichert. Sie können optional einen Beleg anhängen.
            </Text>
            <FileInput
              label="Beleg (PDF oder Bild)"
              placeholder="Datei wählen..."
              accept="application/pdf,image/*"
              value={receiptFile}
              onChange={setReceiptFile}
              leftSection={<IconUpload size={16} />}
              clearable
            />
            <DatePickerInput
              label="Belegdatum (optional)"
              value={documentDate}
              onChange={(v: any) => setDocumentDate(v ? new Date(v) : null)}
              valueFormat="DD.MM.YYYY"
              clearable
            />
            <Group mt="xs">
              <Button
                onClick={handleUpload}
                loading={uploading}
                disabled={!receiptFile}
                leftSection={<IconReceipt size={16} />}
              >
                Hochladen & Fertig
              </Button>
              <Button variant="subtle" onClick={() => navigate('/booking')}>
                Überspringen
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>
      </Stepper>
    </Stack>
  );
}
